import React, { Fragment } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ronFormat, toLocalDateString } from '../../utils/helper';
import logo from '../../assets/images/logo.png';

const SalesInvoicePrint = ({ invoice }) => {

    const { t } = useTranslation();
    const currencyFormat = ronFormat

    const company = invoice?.company

    return (
        <div className="m-3 p-3">
            <Row>
                <Col xs="6">
                    {/* Tenant company details */}
                    {company?.name &&
                        <div>
                            <h4 className="mb-1">{company.name}</h4>
                            {company.address && <p className="mb-0">{company.address}</p>}
                            {company.phone && <p className="mb-0">{company.phone}</p>}
                            {company.email && <p className="mb-0">{company.email}</p>}
                            {company.registrationNumber && <p className="mb-0">{t('invoice.registrationNumber') + ': '}{company.registrationNumber}</p>}
                            {company.taxNumber && <p className="mb-0">{t('invoice.taxNumber') + ': '}{company.taxNumber}</p>}
                            {company.bankAccount && <p className="mb-0">{t('invoice.bankAccount') + ': '}{company.bankAccount}</p>}
                        </div>
                    }
                </Col>
                <Col>
                    <div className="w-100 text-center">
                        {/* Tenant logo when uploaded, otherwise the default logo */}
                        <img className="logo-img px-5" style={{ maxHeight: '150px' }} src={company?.logoUrl || logo} alt={company?.name || 'My ViVo Bot'} />
                    </div>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs="6">
                    <div>
                        <p className="mb-3">{t('invoice.dateOfIssue') + ': '}{toLocalDateString(invoice?.date)}</p>
                        <h3 className="mb-1">{invoice?.clientFirstName + ' ' + invoice?.clientLastName}</h3>
                        <div className="mb-3">
                            {invoice?.clientEmail && <p className="mb-0"><i className="fa-solid fa-envelope text-muted"></i> {invoice?.clientEmail}</p>}
                            {invoice?.clientPhone && <p className="mb-0"><i className="fa-solid fa-phone text-muted"></i> {invoice?.clientPhone}</p>}
                            {invoice?.address && <p className="mb-0"><i className="fa-solid fa-location-dot text-muted"></i> {invoice?.address}</p>}
                        </div>
                    </div>
                </Col>
                <Col>

                    <div className="bg-grey text-center text-white mb-1">{t('invoice.invoiceNumber')}</div>
                    <p className="text-center">{invoice?.invoiceNumber || `INV${invoice?.jobNo ?? ''}`}</p>

                    <table className="w-100">
                        <tbody>
                            <tr>
                                <td>{t('invoice.invoiceCurrency') + ':'}</td>
                                <td className="text-end">RON</td>
                            </tr>
                            <tr>
                                <td>{t('invoice.amount') + ':'}</td>
                                <td className="text-end text-bold">
                                    {currencyFormat(invoice?.totalAmount, false)}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </Col>
            </Row>
            <Row>
                <Table className="mb-4">
                    <thead>
                        <tr>
                            <th>{t('invoice.nr')}</th>
                            <th>{t('invoice.description')}</th>
                            <th>{t('invoice.quantity')}</th>
                            <th>{t('invoice.unitPrice')}</th>
                            <th>{t('invoice.unitOfMeasure')}</th>
                            <th>{t('invoice.priceExclVat')}</th>
                            <th>{t('invoice.vatRate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice?.lineItems.map((lineItem, index) => (
                            <tr key={`invoice-${invoice.jobNo}-lineitem-${index}`}>
                                <td>{lineItem.no ?? index + 1}</td>
                                <td>{lineItem.description}</td>
                                <td>
                                    {lineItem.unitOfMeasure === 'm2' ?
                                        <p className='m-0'>{currencyFormat(lineItem.quantity, false)}
                                            <span className='m-0'>{' '}
                                                ({currencyFormat(lineItem.length, false)} X {currencyFormat(lineItem.width, false)})
                                            </span>
                                        </p>
                                        : currencyFormat(lineItem.quantity, false)
                                    }
                                </td>
                                <td>{currencyFormat(lineItem.unitPrice, false)}</td>
                                <td>{lineItem.unitLabel || (lineItem.unitOfMeasure === 'm2' ? 'm²' : lineItem.unitOfMeasure)}</td>
                                <td>{currencyFormat(lineItem.priceWithoutVat, false)}</td>
                                <td>{lineItem?.vatRate}%</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Row>

            {invoice?.hasM2Items &&
                <Row className="mb-3">
                    <Col xs="12" className="text-end">
                        <p className="mb-0"><strong>{t('invoice.totalAreaM2') + ': '}{currencyFormat(invoice?.totalM2, false)}</strong></p>
                    </Col>
                </Row>
            }

            <Row className="justify-content-end">
                <Col xs="5">
                    <Row className="justify-content-end">
                        <Col className="text-end">
                            <p className="mb-0">{t('invoice.totalExclVat') + ':'}</p>
                        </Col>
                        <Col xs="3" className="text-end">
                            <p className="mb-0">{currencyFormat(invoice?.totalPriceWithoutTax, false)}</p>
                        </Col>
                    </Row>
                    <hr />
                    {invoice?.taxRateGroup.map((vatItem, index) => (
                        (<Fragment key={`withvat-${index}`}>
                            <Row className="justify-content-end">
                                <Col className="text-end">
                                    <p className="mb-0">{t('invoice.totalVatWithRate', { rate: vatItem.taxRate }) + ':'}</p>
                                </Col>
                                <Col xs="3" className="text-end">
                                    <p className="mb-0">{currencyFormat(vatItem.totalTaxAmount, false)}</p>
                                </Col>
                            </Row>
                            <hr />
                        </Fragment>)
                    ))}
                    {/* <Row className="justify-content-end">
                        <Col className="text-end">
                            <p className="mb-0">Sum of items not subject to VAT:</p>
                        </Col>
                        <Col xs="3" className="text-end">
                            <p className="mb-0">{getNoVatItemsSum() ?? '0'}</p>
                        </Col>
                    </Row>
                    <hr /> */}
                    <Row className="justify-content-end">
                        <Col className="text-end">
                            <p className="mb-0">{t('invoice.totalVat') + ':'}</p>
                        </Col>
                        <Col xs="3" className="text-end">
                            <p className="mb-0">{currencyFormat(invoice?.totalTaxAmount, false)}</p>
                        </Col>
                    </Row>
                    <hr />
                    <Row className="justify-content-end">
                        <Col className="text-end">
                            <p className="mb-0">{t('invoice.grandTotal') + ':'}</p>
                        </Col>
                        <Col xs="3" className="text-end">
                            <p className="mb-0">{currencyFormat(invoice?.totalAmount, false)}</p>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default SalesInvoicePrint;